import * as React from "react";
import * as ToastPrimitive from "@radix-ui/react-toast";
import { AlertTriangle, CheckCircle2, Info, X, XCircle } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/utils";

export type ToastVariant = "default" | "success" | "error" | "warning" | "info";

export interface ToastOptions {
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: ToastVariant;
  /** Auto-dismiss ms. 0 = sticky until dismissed. Defaults per variant. */
  duration?: number;
  action?: { label: string; onClick?: () => void };
}

export interface ToastRecord extends ToastOptions {
  id: string;
}

export interface ToastApi {
  toast: (options: ToastOptions) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

const DEFAULT_DURATION: Record<ToastVariant, number> = {
  default: 4500,
  success: 3500,
  info: 4500,
  warning: 5500,
  error: 7000,
};

const MAX_TOASTS = 5;

/* ------------------------------------------------------------------ */
/* Module-level store — lets `toast()` fire from anywhere, even outside */
/* the provider tree (mirrors the shadcn use-toast pattern).           */
/* ------------------------------------------------------------------ */

type Listener = (toasts: ToastRecord[]) => void;

const store = {
  toasts: [] as ToastRecord[],
  listeners: new Set<Listener>(),
};

function emit() {
  for (const l of store.listeners) l([...store.toasts]);
}

let seq = 0;

function addToast(options: ToastOptions): string {
  const id = `asrt-toast-${++seq}-${Date.now().toString(36)}`;
  store.toasts = [...store.toasts, { ...options, id }].slice(-MAX_TOASTS);
  emit();
  return id;
}

function removeToast(id: string) {
  store.toasts = store.toasts.filter((t) => t.id !== id);
  emit();
}

function subscribe(listener: Listener): () => void {
  store.listeners.add(listener);
  return () => store.listeners.delete(listener);
}

/** Imperative API: `toast({ title: "Deployed", variant: "success" })`. */
export function toast(options: ToastOptions): string {
  return addToast(options);
}
toast.success = (options: Omit<ToastOptions, "variant">) => addToast({ ...options, variant: "success" });
toast.error = (options: Omit<ToastOptions, "variant">) => addToast({ ...options, variant: "error" });
toast.warning = (options: Omit<ToastOptions, "variant">) => addToast({ ...options, variant: "warning" });
toast.info = (options: Omit<ToastOptions, "variant">) => addToast({ ...options, variant: "info" });

const api: ToastApi = { toast: addToast, dismiss: removeToast, clear: () => { store.toasts = []; emit(); } };

const ToastContext = React.createContext<ToastApi>(api);

export function useToast(): ToastApi {
  return React.useContext(ToastContext);
}

/* ------------------------------------------------------------------ */
/* Styled primitives                                                   */
/* ------------------------------------------------------------------ */

const toastVariants = cva(
  "group pointer-events-auto relative flex w-full items-start gap-3 overflow-hidden rounded-xl border p-4 pr-8 shadow-panel transition-all duration-200 data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-fade-up data-[state=closed]:opacity-0",
  {
    variants: {
      variant: {
        default: "border-border bg-card text-card-foreground",
        success: "border-success/40 bg-card text-card-foreground",
        error: "border-destructive/40 bg-card text-card-foreground",
        warning: "border-warning/40 bg-card text-card-foreground",
        info: "border-primary/40 bg-card text-card-foreground",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

const Toast = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Root> & VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => (
  <ToastPrimitive.Root
    ref={ref}
    className={cn(toastVariants({ variant }), className)}
    {...props}
  />
));
Toast.displayName = ToastPrimitive.Root.displayName;

const ToastAction = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Action>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Action>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Action
    ref={ref}
    className={cn(
      "inline-flex h-8 shrink-0 items-center justify-center rounded-lg border border-input bg-transparent px-3 text-xs font-medium ring-offset-background transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 group-[.destructive]:border-destructive/40",
      className,
    )}
    {...props}
  />
));
ToastAction.displayName = ToastPrimitive.Action.displayName;

const ToastClose = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Close>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Close
    ref={ref}
    className={cn(
      "absolute right-2 top-2 rounded-md p-1 text-muted-foreground opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      className,
    )}
    toast-close=""
    {...props}
  >
    <X className="size-4" aria-hidden="true" />
    <span className="sr-only">Dismiss notification</span>
  </ToastPrimitive.Close>
));
ToastClose.displayName = ToastPrimitive.Close.displayName;

const ToastTitle = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Title>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Title ref={ref} className={cn("text-sm font-semibold", className)} {...props} />
));
ToastTitle.displayName = ToastPrimitive.Title.displayName;

const ToastDescription = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Description>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
ToastDescription.displayName = ToastPrimitive.Description.displayName;

const ToastViewport = React.forwardRef<
  React.ElementRef<typeof ToastPrimitive.Viewport>,
  React.ComponentPropsWithoutRef<typeof ToastPrimitive.Viewport>
>(({ className, ...props }, ref) => (
  <ToastPrimitive.Viewport
    ref={ref}
    aria-label="Notifications"
    className={cn(
      "fixed bottom-0 right-0 z-[100] flex max-h-screen w-full flex-col-reverse gap-2 p-4 sm:flex-col md:max-w-[420px]",
      className,
    )}
    {...props}
  />
));
ToastViewport.displayName = ToastPrimitive.Viewport.displayName;

const VARIANT_ICON: Record<ToastVariant, React.ReactNode> = {
  default: <Info className="size-4 shrink-0 text-primary motion-safe:animate-fade-in" aria-hidden="true" />,
  info: <Info className="size-4 shrink-0 text-primary" aria-hidden="true" />,
  success: <CheckCircle2 className="size-4 shrink-0 text-success" aria-hidden="true" />,
  warning: <AlertTriangle className="size-4 shrink-0 text-warning" aria-hidden="true" />,
  error: <XCircle className="size-4 shrink-0 text-destructive" aria-hidden="true" />,
};

function ToastItem({ record }: { record: ToastRecord }) {
  const variant = record.variant ?? "default";
  const duration = record.duration ?? DEFAULT_DURATION[variant];

  return (
    <Toast
      variant={variant}
      className={cn(variant === "error" && "group-destructive")}
      duration={duration}
    >
      {VARIANT_ICON[variant]}
      <div className="flex flex-col gap-1">
        {record.title ? <ToastTitle>{record.title}</ToastTitle> : null}
        {record.description ? <ToastDescription>{record.description}</ToastDescription> : null}
        {record.action ? (
          <ToastAction
            altText={record.action.label}
            className="mt-2 self-start"
            onClick={() => record.action?.onClick?.()}
          >
            {record.action.label}
          </ToastAction>
        ) : null}
      </div>
      <ToastClose />
    </Toast>
  );
}

/**
 * Context provider — exposes `useToast()` to descendants. Renders the Radix
 * provider so portals/tooltips inside toasts behave. Pair with `<Toaster />`
 * (mounted once, usually next to the app root) which paints the stack.
 */
const ToastProvider = ({
  children,
  swipeDirection = "right",
}: {
  children?: React.ReactNode;
  swipeDirection?: "right" | "left" | "up" | "down";
}) => (
  <ToastContext.Provider value={api}>
    <ToastPrimitive.Provider swipeDirection={swipeDirection}>{children}</ToastPrimitive.Provider>
  </ToastContext.Provider>
);
ToastProvider.displayName = "ToastProvider";

/** Mount once. Subscribes to the toast store and renders the bottom-right stack. */
const Toaster = () => {
  const [toasts, setToasts] = React.useState<ToastRecord[]>([]);

  React.useEffect(() => subscribe(setToasts), []);

  return (
    <ToastPrimitive.Provider swipeDirection="right">
      {toasts.map((t) => (
        <ToastItem key={t.id} record={t} />
      ))}
      <ToastViewport />
    </ToastPrimitive.Provider>
  );
};
Toaster.displayName = "Toaster";

export {
  Toast,
  ToastAction,
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastTitle,
  ToastViewport,
  Toaster,
  toastVariants,
  ToastPrimitive,
};
